import { motion } from "framer-motion";
import { Workflow, Users2, FlaskConical, Gavel, HeartHandshake } from "lucide-react";

const PILLARS = [
  {
    icon: Workflow,
    label: "Transparent workflows",
    desc: "Every editorial decision is documented, dated and traceable.",
    metric: "100%",
    metricLabel: "Traceability",
  },
  {
    icon: Users2,
    label: "International collaboration",
    desc: "Reviewers and editorial members from 40+ academic institutions.",
    metric: "40+",
    metricLabel: "Countries",
  },
  {
    icon: FlaskConical,
    label: "Evidence-based publishing",
    desc: "Methodological scrutiny aligned with EQUATOR Network guidelines.",
    metric: "EQUATOR",
    metricLabel: "Compliant",
  },
  {
    icon: Gavel,
    label: "Ethical oversight",
    desc: "COPE-member ethics committee adjudicates publication concerns.",
    metric: "COPE",
    metricLabel: "Aligned",
  },
  {
    icon: HeartHandshake,
    label: "Author-centered support",
    desc: "Dedicated editorial liaisons assist throughout the publication lifecycle.",
    metric: "1:1",
    metricLabel: "Author Support",
  },
];

export default function EditorialExcellence() {
  return (
    <section
      data-testid="editorial-excellence-section"
      className="relative py-24 md:py-32 bg-[var(--sci-navy)] text-white overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
      {/* Teal vignette */}
      <div
        className="absolute -right-32 top-1/4 w-[480px] h-[480px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,122,138,0.35), transparent)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start mb-20">
          <div className="lg:col-span-7">
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 06 / Editorial Excellence
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Committed to <em className="not-italic text-[var(--sci-teal)]">quality</em> <br />
              and <em className="not-italic text-[var(--sci-teal)]">transparency</em>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/70 text-base md:text-lg leading-relaxed lg:pt-4">
            Five interconnected pillars uphold the standard that medical and
            surgical research demands — from manuscript intake to long-term
            archival access.
          </p>
        </div>

        {/* Infographic-style grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            const isLast = i === PILLARS.length - 1;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.55, delay: 0.06 * i }}
                data-testid={`pillar-${i}`}
                className={`bg-[var(--sci-navy)] p-8 md:p-10 group hover:bg-[var(--sci-navy-soft)] transition-colors ${
                  isLast ? "lg:col-span-3" : ""
                }`}
              >
                <div className={`${isLast ? "lg:flex lg:items-center lg:justify-between lg:gap-12" : ""}`}>
                  <div className={`${isLast ? "lg:max-w-xl" : ""}`}>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-11 h-11 border border-white/20 flex items-center justify-center text-[var(--sci-teal)]">
                        <Icon size={18} strokeWidth={1.5} />
                      </div>
                      <div className="overline text-white/55">
                        Pillar {String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <h3 className="font-serif-display text-2xl md:text-[28px] leading-tight">
                      {p.label}
                    </h3>
                    <p className="mt-3 text-sm md:text-[15px] text-white/65 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                  <div className={`${isLast ? "mt-8 lg:mt-0 lg:text-right" : "mt-8"} `}>
                    <div className="font-serif-display text-5xl md:text-6xl text-[var(--sci-teal)] leading-none">
                      {p.metric}
                    </div>
                    <div className="overline text-white/50 mt-3">
                      {p.metricLabel}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
